import { Alert, AlertDescription, useToast } from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AppText from "../../components/Core/AppText/AppText";
import Button from "../../components/Core/Button/Button";
import FloatingButtonPanel from "../../components/Shared/FloatingButtonPanel/FloatingButtonPanel";
import OBMatrix, { OBRule, extractOBSalaryCodes } from "../../components/Shared/OBMatrix";
import SimpleConfirmationModal from "../../components/Shared/SimpleConfirmationModal/SimpleConfirmationModal";
import Contract from "../../models/Contract";
import { useBatchUpdateObRules, useObContracts } from "../../networking/contract.hooks";
import { useOrganisation } from "../../networking/organisation.hooks";

interface Props {
	contract: Contract;
}

const ContractOBTab: React.FC<Props> = ({ contract }) => {
	const { data: obRules, isLoading: loading, refetch } = useObContracts(contract.id);
	const { data: organisation } = useOrganisation();
	const toast = useToast();
	const { t } = useTranslation();

	// Extract salary codes from organisation
	const salaryCodes = useMemo(() => {
		return !organisation ? [] : extractOBSalaryCodes(organisation);
	}, [organisation]);

	// State for OBMatrix rules
	const [matrixRules, setMatrixRules] = useState<OBRule[]>([]);
	const [hasChanges, setHasChanges] = useState(false);
	const [isValid, setIsValid] = useState(true);
	const [triggerValidation, setTriggerValidation] = useState<{ fn: () => Promise<boolean> } | null>(
		null
	);
	const [hadExistingRules, setHadExistingRules] = useState(false);
	const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);

	// Transform API data to OBMatrix format
	useEffect(() => {
		if (obRules?.rules) {
			const transformedRules: OBRule[] = obRules.rules.map((rule: any) => {
				// Date rules
				if (rule.date) {
					// Extract date portion from ISO date-time (API returns YYYY-MM-DDTHH:mm:ss.sssZ)
					const dateOnly = rule.date.split("T")[0];
					return {
						mode: "date" as const,
						id: rule.id,
						date: dateOnly,
						name: rule.name || "",
						start: rule.start,
						end: rule.end,
						salarySupplement: rule.salarySupplement || 0,
						invoiceSupplement: rule.invoiceSupplement || 0,
						salaryType: rule.salaryType || null,
					};
				}

				// Weekday rules
				return {
					mode: "weekday" as const,
					id: rule.id,
					weekdays: rule.weekdays,
					name: rule.name || null,
					start: rule.start,
					end: rule.end,
					salarySupplement: rule.salarySupplement || 0,
					invoiceSupplement: rule.invoiceSupplement || 0,
					salaryType: rule.salaryType || null,
				};
			});

			setMatrixRules(transformedRules);
			// Track if we had existing rules from backend
			if (transformedRules.length > 0) {
				setHadExistingRules(true);
			}
		}
	}, [obRules]);

	// Handle matrix changes
	const handleMatrixChange = (rules: OBRule[]) => {
		// If user is removing the last rule and we had existing rules from backend, show confirmation
		if (rules.length === 0 && hadExistingRules && matrixRules.length > 0) {
			setShowDeactivateConfirm(true);
			return;
		}

		setMatrixRules(rules);
		setHasChanges(true);
	};

	// Handle creating first rule
	const handleCreateFirstRule = (mode: "weekday" | "date") => {
		const emptyRule: OBRule =
			mode === "date"
				? {
						mode: "date",
						date: "",
						name: "",
						start: "",
						end: "",
						salarySupplement: null,
						invoiceSupplement: null,
						salaryType: null,
				  }
				: {
						mode: "weekday",
						name: null,
						weekdays: [],
						start: "",
						end: "",
						salarySupplement: null,
						invoiceSupplement: null,
						salaryType: null,
				  };
		setMatrixRules([emptyRule]);
		setHasChanges(true);
	};

	// Batch update mutation
	const { mutate: batchUpdateObRules, isLoading: isSaving } = useBatchUpdateObRules(contract.id);

	// Handle deactivation confirmation
	const handleConfirmDeactivate = () => {
		// Send empty array to backend to deactivate OB rules
		batchUpdateObRules(
			{ rules: [] },
			{
				onSuccess: () => {
					setShowDeactivateConfirm(false);
					toast({
						render: () => (
							<Alert status="success" borderWidth={2} borderRadius={4} variant={"solid"}>
								<AlertDescription>OB-regler inaktiverade</AlertDescription>
							</Alert>
						),
						isClosable: true,
						position: "bottom-right",
						duration: 2000,
					});
					// Reload the full page after successful deactivation
					setTimeout(() => {
						window.location.reload();
					}, 500);
				},
				onError: () => {
					setShowDeactivateConfirm(false);
					toast({
						render: () => (
							<Alert status="error" borderWidth={2} borderRadius={4} variant={"solid"}>
								<AlertDescription>Något gick fel vid inaktivering</AlertDescription>
							</Alert>
						),
						isClosable: true,
						position: "bottom-right",
						duration: 3000,
					});
				},
			}
		);
	};

	// Handle cancel deactivation
	const handleCancelDeactivate = () => {
		setShowDeactivateConfirm(false);
	};

	// Save all OB rules using batch update
	const handleSaveRules = async () => {
		// Trigger validation manually before saving
		if (triggerValidation) {
			const isFormValid = await triggerValidation.fn();
			if (!isFormValid) {
				toast({
					render: () => (
						<Alert status="error" borderWidth={2} borderRadius={4} variant={"solid"}>
							<AlertDescription>{t("FIX_VALIDATION_ERRORS_BEFORE_SAVING")}</AlertDescription>
						</Alert>
					),
					isClosable: true,
					position: "bottom-right",
					duration: 3000,
				});
				return;
			}
		}

		try {
			// Transform OBMatrix rules to API format
			const apiRules = matrixRules.map((rule) => {
				if (rule.mode === "date") {
					// Convert YYYY-MM-DD to ISO date-time format (YYYY-MM-DDTHH:mm:ss.sssZ)
					// Use midnight UTC for the date
					const isoDateTime = `${rule.date}T00:00:00.000Z`;
					return {
						id: rule.id,
						name: rule.name || undefined,
						date: isoDateTime,
						start: rule.start,
						end: rule.end,
						salarySupplement: rule.salarySupplement || 0,
						invoiceSupplement: rule.invoiceSupplement || 0,
						salaryType: rule.salaryType || undefined,
					};
				} else {
					return {
						id: rule.id,
						name: rule.name || undefined,
						weekdays: rule.weekdays,
						start: rule.start,
						end: rule.end,
						salarySupplement: rule.salarySupplement || 0,
						invoiceSupplement: rule.invoiceSupplement || 0,
						salaryType: rule.salaryType || undefined,
					};
				}
			});

			batchUpdateObRules(
				{ rules: apiRules },
				{
					onSuccess: () => {
						toast({
							render: () => (
								<Alert status="success" borderWidth={2} borderRadius={4} variant={"solid"}>
									<AlertDescription>OB-regler sparade</AlertDescription>
								</Alert>
							),
							isClosable: true,
							position: "bottom-right",
							duration: 2000,
						});
						setHasChanges(false);
						refetch();
					},
					onError: () => {
						toast({
							render: () => (
								<Alert status="error" borderWidth={2} borderRadius={4} variant={"solid"}>
									<AlertDescription>Något gick fel vid sparande</AlertDescription>
								</Alert>
							),
							isClosable: true,
							position: "bottom-right",
							duration: 3000,
						});
					},
				}
			);
		} catch (error) {
			toast({
				render: () => (
					<Alert status="error" borderWidth={2} borderRadius={4} variant={"solid"}>
						<AlertDescription>Något gick fel vid sparande</AlertDescription>
					</Alert>
				),
				isClosable: true,
				position: "bottom-right",
				duration: 3000,
			});
		}
	};

	return (
		<div className="max-w-[1200px]">
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					marginTop: "2rem",
					marginBottom: "1rem",
				}}
			>
				<AppText size="m" weight="bold" color="grayText">
					OB-regler
				</AppText>
			</div>
			<div
				style={{
					backgroundColor: "white",
					padding: "1.5rem",
					borderRadius: "4px",
					border: "1px solid #e0e0e0",
				}}
			>
				{loading ? (
					<div style={{ padding: "2rem", textAlign: "center" }}>
						<AppText>Laddar OB-regler...</AppText>
					</div>
				) : matrixRules.length === 0 ? (
					<div className="flex flex-col items-center justify-center py-16 px-4">
						<svg
							className="w-16 h-16 text-gray-400 mb-4"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={1}
								d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
							/>
						</svg>
						<AppText size="l" weight="bold" color="grayText" margin="0 0 0.5rem 0">
							Inga OB-regler
						</AppText>
						<AppText size="s" color="grayText" textAlign="center" margin="0 0 2rem 0">
							Skapa din första OB-regel för att komma igång
						</AppText>
						<div className="flex gap-3">
							<Button
								type="button"
								onClick={() => handleCreateFirstRule("weekday")}
								disabled={isSaving}
								backgroundColor="buttonGray"
								textColor="black"
								fontSize="s"
								height="34px"
							>
								+ Regel för <u>veckodag</u>
							</Button>
							<Button
								type="button"
								onClick={() => handleCreateFirstRule("date")}
								disabled={isSaving}
								backgroundColor="buttonGray"
								textColor="black"
								fontSize="s"
								height="34px"
							>
								+ Regel för <u>datum</u>
							</Button>
						</div>
						{/* <AppText size="s" margin="1rem 0 0 0" color="grayText">
							Eller
						</AppText> */}

						<Button
							type="button"
							onClick={() => handleCreateFirstRule("date")}
							disabled={isSaving}
							backgroundColor="transparent"
							textColor="blueLight"
							fontSize="s"
							height="34px"
							margin="0.5rem 0 0 0"
						>
							<u>Kopiera från annat avtal</u>
						</Button>
					</div>
				) : (
					<>
						<OBMatrix
							value={matrixRules}
							onChange={handleMatrixChange}
							onValidationChange={setIsValid}
							onValidationTrigger={setTriggerValidation}
							disabled={isSaving}
							salaryCodes={salaryCodes}
						/>
						{hasChanges && (
							<FloatingButtonPanel transparent={false}>
								<Button
									onClick={handleSaveRules}
									disabled={isSaving}
									backgroundColor="primary"
									textColor="white"
									fontSize="m"
								>
									{isSaving ? "Sparar..." : "Spara ändringar"}
								</Button>
							</FloatingButtonPanel>
						)}
					</>
				)}
			</div>

			{/* Deactivation Confirmation Modal */}
			{showDeactivateConfirm && (
				<SimpleConfirmationModal
					title="Inaktivera OB-regler"
					bodyText="Är du säker på att du vill inaktivera alla OB-regler för detta avtal?"
					onConfirmation={handleConfirmDeactivate}
					onCancel={handleCancelDeactivate}
					confirmButtonText="Ja, inaktivera OB"
					cancelButtonText="Avbryt"
					isLoading={isSaving}
				/>
			)}
		</div>
	);
};

export default ContractOBTab;
